import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO, PHONE } from "../lib/api";
import { NAV_LINKS } from "../data/content";

const nt = { target: "_blank", rel: "noopener noreferrer" };

export function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#050505]/85 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO} alt="The Pink Crew" className="h-10 md:h-12 w-auto rounded-md" />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} {...nt} className={`text-sm font-medium transition-colors hover:text-white ${loc.pathname === l.to ? "text-white" : "text-zinc-400"}`} data-testid={`nav-${l.label.toLowerCase().replace(/ /g,'-')}`}>{l.label}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={`tel:${PHONE}`} className="hidden sm:flex items-center gap-2 text-sm text-zinc-300 hover:text-white"><Phone size={16} />{PHONE}</a>
          <Link to="/book" {...nt} className="bg-pink-600 hover:bg-pink-500 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors active:scale-95" data-testid="header-book-btn">Book Now</Link>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#050505] px-4 py-4 flex flex-col gap-4">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} {...nt} onClick={() => setOpen(false)} className="text-zinc-300 hover:text-white">{l.label}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/10 bg-[#050505] mt-24">
      <div className="max-w-7xl mx-auto px-6 py-14 grid gap-10 md:grid-cols-4">
        <div>
          <img src={LOGO} alt="The Pink Crew" className="h-14 w-auto rounded-md mb-4" />
          <p className="text-sm text-zinc-400 leading-relaxed">Luxury mobile car detailing serving Windward Oahu — Kailua, Kaneohe, Lanikai, Enchanted Lakes & Waimanalo.</p>
        </div>
        <div>
          <h4 className="text-sm font-bold uppercase tracking-widest text-pink-500 mb-4">Services</h4>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/services/interior" {...nt} className="hover:text-white">Interior Detail</Link></li>
            <li><Link to="/services/exterior" {...nt} className="hover:text-white">Exterior Detail</Link></li>
            <li><Link to="/services/full" {...nt} className="hover:text-white">Full Detail</Link></li>
            <li><Link to="/pricing" {...nt} className="hover:text-white">Pricing</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold uppercase tracking-widest text-pink-500 mb-4">Add-ons & Plans</h4>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/add-ons/exterior" {...nt} className="hover:text-white">Exterior Add-ons</Link></li>
            <li><Link to="/add-ons/interior" {...nt} className="hover:text-white">Interior Add-ons</Link></li>
            <li><Link to="/add-ons/signature" {...nt} className="hover:text-white">Signature Add-ons</Link></li>
            <li><Link to="/maintenance" {...nt} className="hover:text-white">Maintenance Plans</Link></li>
            <li><Link to="/custom-plan" {...nt} className="hover:text-white">Customizable Plan</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold uppercase tracking-widest text-pink-500 mb-4">Company</h4>
          <ul className="space-y-2 text-sm text-zinc-400">
            <li><Link to="/reviews" {...nt} className="hover:text-white">Reviews</Link></li>
            <li><Link to="/terms" {...nt} className="hover:text-white">Terms of Service</Link></li>
            <li><a href={`tel:${PHONE}`} className="hover:text-white">Call {PHONE}</a></li>
            <li><Link to="/book" {...nt} className="hover:text-white">Book Now</Link></li>
            <li><Link to="/reschedule" {...nt} className="hover:text-white">Reschedule Booking</Link></li>
            <li><Link to="/admin" {...nt} className="hover:text-white text-zinc-600">Console</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-zinc-600">© {new Date().getFullYear()} The Pink Crew Mobile Detailing. All rights reserved.</div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App min-h-screen bg-[#050505]">
      <Header />
      <main className="pt-16 md:pt-20">{children}</main>
      <Footer />
    </div>
  );
}
